import { WorkspaceEnrichPanelView, MOTION_EASE, MOTION_MS, DESCRIPTION } from "./WorkspaceEnrichPanelView";
import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, InputBase, LinearProgress, Typography, useTheme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { PRIMARY_ENRICHMENT_PROMPTS, findSuggestedEnrichmentPromptByText } from "../../helper/suggestedEnrichmentPrompts";
import { getDarkModeEnrichPanelGradient } from "../../theme/colorTokens";
import { useCreditsBalance } from "../../context/CreditsBalanceContext";
import { buildActionCreditEstimate, isCreditBalanceResolved } from "../../helper/actionCreditEstimate";
import ActionCreditUsageBanner from "../../components/layouts/ActionCreditUsageBanner";
import { formatCreditCount } from "../../helper/creditBalanceModel";
import AiProviderIconStack from "./AiProviderIconStack";
import SuggestedEnrichmentPrompts from "./SuggestedEnrichmentPrompts";
import { getWorkspaceEnrichmentTitleCollision, normalizeWorkspaceEnrichmentTitle } from "./savedSearchQuery";
import { WORKSPACE_ENRICH_CONTROL_HEIGHT as CONTROL_HEIGHT, WorkspaceEnrichCombinedInput } from "./WorkspaceEnrichControls";

const BANNER_BASE = "#F6F4FF";
const LOGO_OVERLAP_COMPACT = "-12px";
const LOGO_OVERLAP_SPREAD = "-1px";

/**
 * Enrich Workspace V2 / V3 — compact single-row rest state + hover/focus expand:
 *   Desktop rest — icons | title | input+model | CTA (one line, shorter banner)
 *   Desktop hover / focus-within — logos stacked above title + description; chips reveal
 *   Mobile — stacked column: identity → input+model → prompts (expanded) → CTA
 *
 * variant="v2" — lavender card + outline (default)
 * variant="v3" — white card + drop shadow (sits on the page’s extended lavender band)
 */

export default function WorkspaceEnrichPanelV2({
  enrichQuery,
  setEnrichQuery,
  columnTitle = "",
  setColumnTitle = () => {},
  llmProvider,
  setLlmProvider,
  enriching = false,
  batchStatus = null,
  disabled = false,
  selectedCount = 0,
  enrichmentScopeCount = selectedCount,
  onEnrich,
  /** FE #588: useEnrichmentQuote() for this run. Nothing is priced or submittable until it is "ready". */
  enrichQuote = null,
  workspaceEnrichmentColumns = [],
  workspaceEnrichmentColumnLabels = {},
  title = "Enrich Workspace",
  variant = "v2",
  /** Mobile-only: compact mode (input stays visible; description and chips hidden). */
  collapsed = false,
  onExpand,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isDark = theme.palette.mode === "dark";
  const isV3 = variant === "v3";
  const controlsDisabled = disabled || enriching;
  const { balance, loading, refreshing, error } = useCreditsBalance();
  const availableCredits = balance?.totalAvailable ?? null;
  const balanceResolved = isCreditBalanceResolved({ availableCredits, loading, refreshing, error });
  // The backend's quote (BE #517) is the price: its upper bound for exactly this company set and model.
  const quote = enrichQuote?.status === "ready" ? enrichQuote.quote : null;
  const creditEstimate = buildActionCreditEstimate({ actionId: "ai_enrichment",
    selectedCount: quote?.company_count ?? 0, creditsPerUnit: quote?.credits_per_company ?? 0, availableCredits });
  // The page quotes the default scope on load. A refusal (e.g. the freemium trial run
  // cap on an all-rows scope) is only news once the user has asked for an enrichment;
  // before that it contradicted the free onboarding run on every row.
  const hasEnrichRequest = Boolean(String(enrichQuery || "").trim());
  // FE #748: the button shows the quote only for the exact scope it priced. Visual only:
  // screen readers already hear the same price from the credit usage banner beside it.
  const buttonCost = quote && quote.company_count === enrichmentScopeCount && Number.isFinite(quote.max_credits)
    ? <span aria-hidden="true" data-testid="workspace-enrich-button-cost">{` · up to ${formatCreditCount(quote.max_credits)} credits`}</span>
    : null;
  const showQuoteRefusal = enrichQuote?.status === "error" && hasEnrichRequest;
  const creditUsageBanner = quote ? <ActionCreditUsageBanner actionId="ai_enrichment"
    selectedCount={quote.company_count} creditsPerUnit={quote.credits_per_company}
    testId="workspace-enrich-credit-usage" /> : (enrichQuote?.status === "loading" || showQuoteRefusal) && (
    <Typography variant="caption" color={showQuoteRefusal ? "error" : "text.secondary"}
      role={showQuoteRefusal ? "alert" : undefined} data-testid="workspace-enrich-quote-status">
      {showQuoteRefusal ? enrichQuote.message : "Calculating credit cost…"}
    </Typography>);
  const stroke = isDark ? "rgba(255,255,255,0.14)" : "rgba(88,89,91,0.16)";
  const [selectedSuggestionId, setSelectedSuggestionId] = useState(null);
  const [combinedFocused, setCombinedFocused] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const effectiveMobileExpanded = !collapsed && mobileExpanded;
  const titleCollision = getWorkspaceEnrichmentTitleCollision(columnTitle,
    workspaceEnrichmentColumns, workspaceEnrichmentColumnLabels);

  useEffect(() => {
    if (collapsed) {
      setMobileExpanded(false);
    }
  }, [collapsed]);

  useEffect(() => {
    const match = findSuggestedEnrichmentPromptByText(enrichQuery);
    setSelectedSuggestionId(match?.id || null);
  }, [enrichQuery]);

  const handleSelectSuggestion = (prompt) => {
    if (!prompt || controlsDisabled) return;
    if (selectedSuggestionId === prompt.id) {
      setSelectedSuggestionId(null);
      if (findSuggestedEnrichmentPromptByText(enrichQuery)?.id === prompt.id) {
        setEnrichQuery("");
      }
      return;
    }
    setSelectedSuggestionId(prompt.id);
    setEnrichQuery(prompt.text);
    if (isMobile) {
      setMobileExpanded(true);
    }
  };

  const submitDisabled = controlsDisabled || Boolean(titleCollision)
    || !String(enrichQuery || "").trim() || !balanceResolved || !quote || creditEstimate.hasShortfall;

  const handleEnrichClick = () => {
    if (submitDisabled) return;
    onEnrich?.();
  };

  const activateMobileInteraction = () => {
    if (isMobile && !controlsDisabled) {
      setMobileExpanded(true);
      if (collapsed) {
        onExpand?.();
      }
    }
  };

  const renderCombinedInput = ({
    multiline = false,
    compactCredits = false,
    showColumnTitle = true,
    onActivate,
    sx: sxProp = {},
  } = {}) => (
    <WorkspaceEnrichCombinedInput
      enrichQuery={enrichQuery}
      setEnrichQuery={setEnrichQuery}
      columnTitle={columnTitle}
      setColumnTitle={setColumnTitle}
      llmProvider={llmProvider}
      setLlmProvider={setLlmProvider}
      disabled={controlsDisabled}
      titleCollision={titleCollision}
      isDark={isDark}
      stroke={stroke}
      focused={combinedFocused}
      setFocused={setCombinedFocused}
      multiline={multiline}
      compactCredits={compactCredits}
      showColumnTitle={showColumnTitle}
      onActivate={onActivate}
      sx={sxProp}
    />
  );

  const renderEnrichButton = ({ fullWidth = false, sx: sxProp = {} } = {}) => (
    <Button
      variant="contained"
      disableElevation
      onClick={handleEnrichClick}
      disabled={submitDisabled}
      sx={{
        width: fullWidth ? "100%" : { sm: "auto" },
        minWidth: fullWidth ? 0 : { sm: 118 },
        height: fullWidth ? 44 : { sm: "100%" },
        minHeight: fullWidth ? 44 : { sm: CONTROL_HEIGHT },
        maxHeight: fullWidth ? 44 : { sm: CONTROL_HEIGHT },
        boxSizing: "border-box",
        m: 0,
        py: 0,
        px: fullWidth ? 2 : { sm: 2 },
        lineHeight: 1.25,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "12px",
        textTransform: "none",
        fontWeight: 700,
        fontSize: fullWidth ? 13 : { sm: 13 },
        whiteSpace: "nowrap",
        ...sxProp,
      }}
    >
      {enriching ? (
        <CircularProgress size={18} color="inherit" />
      ) : selectedCount > 0 ? (
        <>{`Enrich ${selectedCount}`}{buttonCost}</>
      ) : (
        <>Enrich Page{buttonCost}</>
      )}
    </Button>
  );

  const renderProgress = () =>
    enriching && batchStatus ? (
      <Box sx={{ position: "relative", mt: 1.5 }}>
        <Typography variant="body2" sx={{ mb: 0.75, fontSize: 13 }}>
          Enrichment in progress: {batchStatus.completed} / {batchStatus.total} completed
        </Typography>
        <LinearProgress
          variant="determinate"
          value={batchStatus.total ? (batchStatus.completed / batchStatus.total) * 100 : 0}
        />
      </Box>
    ) : null;

  const buildShellSx = ({ mobileExpanded: expanded = false, overflow = "hidden" } = {}) => ({
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 0,
    px: isMobile ? 2 : { xs: 1.5, sm: 2 },
    py: isMobile ? (expanded ? 1.75 : 1.25) : { xs: "10px", sm: "11px" },
    borderRadius: "16px",
    overflow,
    "--ai-provider-overlap": isMobile || expanded ? LOGO_OVERLAP_SPREAD : LOGO_OVERLAP_COMPACT,
    transition: [
      `padding ${MOTION_MS} ${MOTION_EASE}`,
      `box-shadow ${MOTION_MS} ${MOTION_EASE}`,
    ].join(", "),
    ...(isV3
      ? {
          border: "none",
          background: isDark ? "#1E1E1E" : "#FFFFFF",
          backgroundColor: isDark ? "#1E1E1E" : "#FFFFFF",
          boxShadow: isDark
            ? "0 10px 32px rgba(0, 0, 0, 0.55), 0 2px 8px rgba(0, 0, 0, 0.35)"
            : "0 10px 32px rgba(74, 44, 245, 0.12), 0 2px 8px rgba(23, 15, 74, 0.08)",
          "&::before": { display: "none" },
        }
      : {
          border: isDark
            ? "1px solid rgba(255,255,255,0.1)"
            : "1px solid rgba(74,44,245,0.12)",
          boxShadow: isDark
            ? "0 8px 24px rgba(0,0,0,0.35)"
            : "0 8px 24px rgba(23,15,74,0.08)",
          background: isDark
            ? getDarkModeEnrichPanelGradient()
            : `linear-gradient(180deg, ${BANNER_BASE} 0%, ${BANNER_BASE} 42%, ${BANNER_BASE} 100%)`,
          backgroundColor: isDark ? undefined : BANNER_BASE,
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            display: isDark ? "none" : "block",
            background:
              "radial-gradient(ellipse 70% 80% at 8% 20%, rgba(74,44,245,0.14), transparent 55%)",
          },
        }),
    "@media (prefers-reduced-motion: reduce)": {
      transition: "none",
      "& [data-enrich-prompts], & [data-enrich-description]": {
        transition: "none",
      },
    },
  });

  if (isMobile) {
    return (
      <Box
        data-testid={isV3 ? "workspace-enrichment-panel-v3" : "workspace-enrichment-panel-v2"}
        data-mobile-expanded={effectiveMobileExpanded ? "true" : "false"}
        sx={buildShellSx({
          mobileExpanded: effectiveMobileExpanded,
          overflow: "visible",
        })}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 1.25,
              minWidth: 0,
            }}
          >
            <AiProviderIconStack
              selectedProviderId={llmProvider}
              onSelectProvider={setLlmProvider}
              disabled={controlsDisabled}
            />
            <Typography
              variant="subtitle1"
              sx={{
                m: 0,
                fontWeight: 700,
                fontSize: 14,
                lineHeight: 1.2,
                letterSpacing: "-0.02em",
                color: isDark ? "#FFFFFF" : "#171717",
              }}
            >
              {title}
            </Typography>
          </Box>

          {effectiveMobileExpanded ? (
            <Typography
              data-enrich-description=""
              variant="body2"
              sx={{
                m: 0,
                fontSize: 12,
                fontWeight: 400,
                lineHeight: 1.45,
                color: isDark ? "rgba(255,255,255,0.62)" : "#6B6C70",
              }}
            >
              {DESCRIPTION}
            </Typography>
          ) : null}

          {renderCombinedInput({
            multiline: false,
            compactCredits: true,
            showColumnTitle: false,
            onActivate: activateMobileInteraction,
          })}

          {effectiveMobileExpanded || columnTitle ? (
            <InputBase
              value={columnTitle}
              onChange={(event) => setColumnTitle(event.target.value)}
              onBlur={() => setColumnTitle(normalizeWorkspaceEnrichmentTitle(columnTitle))}
              disabled={controlsDisabled}
              placeholder={enrichQuery}
              inputProps={{
                "aria-label": "Column title (optional)",
                "aria-invalid": Boolean(titleCollision),
              }}
              sx={{
                width: "100%",
                minHeight: 40,
                px: 1.25,
                borderRadius: "10px",
                border: `1px solid ${titleCollision ? theme.palette.error.main : stroke}`,
                bgcolor: isDark ? "rgba(22,22,22,0.92)" : "#FFFFFF",
                fontSize: 13,
              }}
            />
          ) : null}

          {titleCollision ? (
            <Typography role="alert" variant="caption" color="error">
              “{titleCollision}” is already used. Pick another column title.
            </Typography>
          ) : null}

          <Box
            data-enrich-prompts=""
            data-testid="enrich-prompts-slot"
            sx={{
              width: "100%",
              display: effectiveMobileExpanded ? "block" : "none",
              mt: effectiveMobileExpanded ? -0.25 : 0,
            }}
          >
            <SuggestedEnrichmentPrompts
              prompts={PRIMARY_ENRICHMENT_PROMPTS}
              selectedId={selectedSuggestionId}
              onSelect={handleSelectSuggestion}
              disabled={controlsDisabled}
              align="start"
              chipLabelNoWrap
              gap={0.75}
            />
          </Box>

          {renderEnrichButton({ fullWidth: true })}
        </Box>

        {creditUsageBanner}
        {renderProgress()}
      </Box>
    );
  }

  const desktopExpandedSx = {
    "--ai-provider-overlap": LOGO_OVERLAP_SPREAD,
    px: { xs: 2, sm: 2.5 },
    py: { xs: 2, sm: 2.25 },
    overflow: "visible",
    "& [data-enrich-layout]": {
      height: "auto",
      minHeight: 0,
      overflow: "visible",
      columnGap: { xs: 2, md: 2.75 },
      rowGap: 2,
      alignItems: "center",
    },
    "& [data-enrich-identity]": {
      height: "auto",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 1.25,
      flex: "0 1 auto",
      maxWidth: { xs: "100%", md: 280 },
    },
    "& [data-enrich-title]": {
      fontSize: 15,
      whiteSpace: "normal",
    },
    "& [data-enrich-description]": {
      position: "static",
      visibility: "visible",
      height: "auto",
      opacity: 1,
      maxHeight: 72,
      marginTop: "0.65rem",
      transform: "translateY(0)",
      pointerEvents: "auto",
    },
    "& [data-enrich-controls]": {
      gridTemplateRows: {
        sm: `${CONTROL_HEIGHT}px auto`,
      },
      rowGap: { sm: 0 },
    },
    "& [data-enrich-prompts]": {
      height: "auto",
      opacity: 1,
      maxHeight: "none",
      overflow: "visible",
      transform: "none",
      marginTop: "12px",
      paddingTop: "6px",
      pointerEvents: "auto",
    },
  };

  const cardSx = {
    ...buildShellSx(),
    "&:hover, &:focus-within": desktopExpandedSx,
  };

  return <WorkspaceEnrichPanelView {...{
      isV3, cardSx, llmProvider, setLlmProvider,
      controlsDisabled, isDark, title, renderCombinedInput,
      selectedSuggestionId, handleSelectSuggestion, renderEnrichButton, titleCollision,
      creditUsageBanner, renderProgress
    }} />;
}
